import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { networkApi } from '../../api/network.js';
import { EmptyBox, ErrorBox, Field, Pagination, SearchInput, Spinner } from '../../components/ui.js';
import { num, text } from '../../lib/format.js';
import { useUrlSearchFields, useUrlUpdate } from '../../lib/hooks.js';
import {
  AgentLabel,
  HostLink,
  MonthSelect,
  PER_PAGE,
  SiteBadge,
  descendingNumber,
  monthBounds,
  monthLabel,
} from './shared.js';

/**
 * 리퍼러 통계 — 레거시 /xadm/report_network_referer_group.php.
 *
 * 유입 로그를 (달·파트너·도메인·기기)로 미리 세어 둔 표다
 * (ALK_REFERER_network_group). 적재는 2023-04 에 멈췄다.
 *
 * 원본에 날짜 입력칸이 없다 - 코드에는 `regdate BETWEEN` 을 붙이는 자리가
 * 있지만 이 테이블에 regdate 컬럼 자체가 없어서, 주소로 날짜를 넣으면 조회가
 * 깨지고 평소에는 조건이 붙지 않았다. 그래서 달 조건만 옮겼다.
 */

export function RefererStatsPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const [filters, setFilter] = useUrlSearchFields(params, update, ['partner', 'refhost']);

  const searchYm = params.get('searchYm') ?? '';
  const page = Number(params.get('page') || '1');

  const months = useQuery({
    queryKey: ['network', 'referer-stats', 'months'],
    queryFn: networkApi.refererStatsMonths,
    staleTime: 10 * 60_000,
  });

  const list = useQuery({
    queryKey: ['network', 'referer-stats', { searchYm, ...filters, page }],
    queryFn: () =>
      networkApi.refererStats({
        searchYm: searchYm || undefined,
        partner: filters.partner || undefined,
        refhost: filters.refhost || undefined,
        page,
        perPage: PER_PAGE,
      }),
    placeholderData: keepPreviousData,
  });

  const data = list.data;
  const shownYm = searchYm || data?.searchYm || '';

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            리퍼러 통계
            {list.isFetching && <span className="page-head__busy">갱신 중…</span>}
          </h1>
          <p className="page-desc">
            달·파트너·도메인·기기별 접속 횟수입니다. 접속횟수가 많은 순으로 보여줍니다 — 적재는 2023-04 에
            멈춰 있습니다.
          </p>
        </div>
      </header>

      <div className="filters">
        <Field label="조회 월">
          <MonthSelect
            months={months.data?.months ?? []}
            value={shownYm}
            onChange={(v) => update({ searchYm: v })}
          />
        </Field>
        <Field label="파트너 아이디 (정확히 일치)">
          <SearchInput
            value={filters.partner ?? ''}
            placeholder="bnn_pop_popcash_592832"
            onChange={(v) => setFilter('partner', v)}
          />
        </Field>
        <Field label="리퍼러 검색 (도메인)">
          <SearchInput
            value={filters.refhost ?? ''}
            placeholder="chinafemdom.net"
            onChange={(v) => setFilter('refhost', v)}
          />
        </Field>
        <button
          type="button"
          className="btn btn--ghost filters__reset"
          onClick={() => setParams(new URLSearchParams(), { replace: true })}
        >
          조건 초기화
        </button>
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : data && data.items.length === 0 ? (
        <EmptyBox>조건에 맞는 집계가 없습니다.</EmptyBox>
      ) : (
        data && (
          <>
            <div className="table-wrap">
              <table className="table network-table">
                <thead>
                  <tr>
                    <th className="num">번호</th>
                    <th>년월</th>
                    <th>파트너ID</th>
                    <th>리퍼러 도메인</th>
                    <th>접속기기</th>
                    <th className="num">접속횟수</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row, i) => (
                    <tr key={row.uid}>
                      <td className="num td-muted">{descendingNumber(data.total, data.page, data.perPage, i)}</td>
                      <td>{monthLabel(row.yearmonth)}</td>
                      <td>
                        <code className="code">{text(row.partner)}</code>
                      </td>
                      <td className="network-host">
                        <SiteBadge site={row.site} />
                        <HostLink host={row.refhost} />
                        {row.refhost && <DetailSearchLink yearmonth={row.yearmonth} refhost={row.refhost} />}
                      </td>
                      <td>
                        <AgentLabel agent={row.agent} />
                      </td>
                      <td className="num">{num(row.cnt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              onChange={(p) => update({ page: p }, false)}
            />
          </>
        )
      )}
    </section>
  );
}

/**
 * '상세검색' — 원본 :190 은 리퍼러 목록을 그 도메인·그 달로 열었다.
 *
 * 원본 링크는 달만 넘겨서, 목록 화면이 자기 기본 기간(이틀 전~오늘)을 덧씌우는
 * 바람에 늘 빈 표가 나왔다. 여기서는 그 달의 1일~말일을 같이 넘겨 실제로
 * 그 달의 유입이 보이게 한다.
 */
function DetailSearchLink({ yearmonth, refhost }: { yearmonth: string; refhost: string }) {
  const { from, to } = monthBounds(yearmonth);
  const search = new URLSearchParams({ searchYm: yearmonth, refhost, from, to });
  return (
    <Link className="network-mini" to={`/network/referers?${search.toString()}`}>
      [상세검색]
    </Link>
  );
}
